import { makeStyles } from 'tss-react/mui';
import { Divider, Grid, Theme, Typography } from '@mui/material';
import React, { ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Panel from '../../../../components/panel/Panel';
import ForecastSpecialClassificationLoansTable from '../../../../containers/tables/ForecastSpecialClassificationLoansTable';
import { ApplicationState } from '../../../../store';
import { ceclstateForecastSingleSpecialLoanUpdate } from '../../../../store/ceclapi/actions';
import { CeclApiState, ForecastSpecialClassificationLoan, ValidationError } from '../../../../store/ceclapi/types';
import { colors } from '../../../../theme';

const useStyles = makeStyles()((theme: Theme) =>
    ({
        paddingLess: {
            padding: 0,
        },

        title: {
            width: '100%',
            textAlign: 'left',
            paddingLeft: theme.spacing(2),
        },

        dialogDivider: {
            color: colors.black,
        },

        disclaimers: {
            margin: theme.spacing(2),
        },

        asteriskText: {
            fontStyle: 'italic',
        },

        secondHeader: {
            fontWeight: 'bold',
            backgroundColor: colors.lightGray4,
            textAlign: 'center',
        }
    }));

export interface ForecastSpecialClassificationLoanProps{
    specialLoansWithErrors?: ValidationError[];
    specialClassificationLoansFromCsv?: ForecastSpecialClassificationLoan[];
}

const ForecastSpecialClassificationLoans: React.FC<ForecastSpecialClassificationLoanProps> = (props) => {
    const cecl = useSelector<ApplicationState, CeclApiState>((state) => state.cecl);
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [specialClassificationLoans, setSpecialClassificationLoans] = React.useState(
        [] as ForecastSpecialClassificationLoan[]
    );

    const specialClassificationLoansFromCsv = props.specialClassificationLoansFromCsv;

    React.useEffect(()=> {
        if(specialClassificationLoansFromCsv){
            setSpecialClassificationLoans(specialClassificationLoansFromCsv);

            // Update the redux store to prevent the data from becoming stale
            specialClassificationLoansFromCsv.forEach(loan => {
                dispatch(ceclstateForecastSingleSpecialLoanUpdate(loan));
            });
        }
    }, [specialClassificationLoansFromCsv, dispatch])

    React.useEffect(() => {
        if (cecl.forecastAssumptions?.configuration?.config?.specialClassificationLoans) {
            setSpecialClassificationLoans([
                ...cecl.forecastAssumptions!.configuration!.config!.specialClassificationLoans!,
            ]);
        }
    }, []);

    React.useEffect(() => {
        if (cecl.forecastAssumptions?.configuration?.config?.specialClassificationLoans) {
            setSpecialClassificationLoans(
                JSON.parse(JSON.stringify(cecl.forecastAssumptions!.configuration!.config!.specialClassificationLoans!))
            );
        }
    }, [cecl.forecastAssumptions?.configuration]);

    React.useEffect(() => {
        if (!specialClassificationLoans.length || !cecl.forecastAssumptions?.detail?.data) {
            return;
        }
        const currentLoans = JSON.parse(JSON.stringify(cecl.forecastAssumptions!.configuration.config.specialClassificationLoans));
        const newLoans = [...cecl.forecastAssumptions!.detail!.data.specialClassificationLoans!];

        for (let i = 0; i < newLoans!.length; i++) {
            for (let j = 0; j < currentLoans!.length; j++) {
                if (newLoans![i].name == currentLoans![j].name) {
                    currentLoans![j] = newLoans![i];
                }
            }
        }

        setSpecialClassificationLoans(currentLoans);
    }, [cecl.forecastAssumptions?.detail]);

    React.useEffect(() => {
        const mySpecialLoans = [...specialClassificationLoans];

        if(mySpecialLoans.length == 0){
            return;
        }
     
        mySpecialLoans.forEach(specialLoans => {
            specialLoans.error = false
        });

        props.specialLoansWithErrors?.map(specialLoanError => specialLoanError.fieldName).forEach(specialLoanName => {
            const specialLoanIndex = mySpecialLoans.findIndex(specialLoan => specialLoan.name == specialLoanName);
            if(specialLoanIndex >= 0){
                mySpecialLoans[specialLoanIndex].error = true;
            } 
        });

        setSpecialClassificationLoans(mySpecialLoans);
    }, [props.specialLoansWithErrors])

    function handleTableEdit(event: any, rowIndex: number, colName: string) {        
        const loanIndex = specialClassificationLoans.findIndex((loan) => loan.order == rowIndex + 1);
        if (loanIndex < 0) {
            return;
        }

        const value = parseFloat((event as unknown) as string);
        const newLoans = [...specialClassificationLoans];
        switch (colName) {
            case 'Specific Loan Loss Ending Balances':
                newLoans[loanIndex].specificLoanLossEndingBalance = value;
                break;
            case 'Allocation':
                newLoans[loanIndex].allocation = value;
                break;
            default:
                break;
        }
        const loan = newLoans[loanIndex];
        dispatch(ceclstateForecastSingleSpecialLoanUpdate(loan));
        setSpecialClassificationLoans(newLoans);
    }

    return (
        <div>
            <Panel className={classes.paddingLess}>
                <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <Typography className={classes.title} variant='h1'>
                            Special Classification Loans / Carve Outs (Optional)
                        </Typography>
                        <Divider variant="middle" className={classes.dialogDivider} />
                    </Grid>
                    <Grid item>
                        <Grid item container direction="row" justifyContent="flex-end">
                            <Grid item xs={5}></Grid>
                            <Grid item xs={7}>
                                <Typography className={classes.secondHeader}>
                                    Special Classification Loans***
                                </Typography>
                            </Grid>
                        </Grid>
                        <ForecastSpecialClassificationLoansTable
                            specialClassificationLoans={specialClassificationLoans}
                            handleTableEdit={handleTableEdit}
                        />
                    </Grid>
                    <Grid item xs={12} className={classes.disclaimers}>
                        <Typography className={classes.asteriskText}>
                            *** Balances entered represent specific loan carve outs that the credit union desires to
                            allocate specific losses for. The amounts classified here will be excluded from the loss
                            forecast.
                        </Typography>
                    </Grid>
                </Grid>
            </Panel>
        </div>
    );
};

export default ForecastSpecialClassificationLoans;
